/**
 * Always-visible data freshness strip.
 *
 * Written because an empty dashboard is ambiguous in the worst way: zeros can
 * mean "no business happened", "the extract has not run yet", or "the extract
 * is broken", and nothing on screen distinguished them. Coverage detail lives
 * in a collapsible; *whether the data is arriving at all* must not.
 *
 * It answers three questions at a glance: when the warehouse was last loaded,
 * whether a load is running now, and when the next one is due.
 */

import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw } from "lucide-react";

import { api } from "@/api/client";
import { ago, count, dateTime } from "@/utils/format";

interface SourceStatus {
  status: string;
  last_finished: string | null;
  rows: number;
}

interface RefreshStatus {
  warehouse_loaded: boolean;
  sources: Record<string, SourceStatus>;
  oldest_finished: string | null;
  newest_finished: string | null;
  any_running: boolean;
  any_error: boolean;
  any_interrupted: boolean;
  refresh_interval_seconds: number;
  next_due_seconds: number | null;
  total_rows: number;
}

export function FreshnessBar() {
  const { data, isLoading } = useQuery({
    queryKey: ["refresh-status"],
    queryFn: async () => (await api.get<RefreshStatus>("/refresh/status")).data,
    // Polled independently of the dashboards so the strip stays live even while
    // a heavy page is still resolving.
    refetchInterval: 20_000,
    refetchOnWindowFocus: true,
  });

  if (isLoading || !data) {
    return (
      <div className="px-5 py-1.5 border-b border-rule bg-surface-alt">
        <div className="skeleton h-3 w-64" />
      </div>
    );
  }

  // Never loaded is a different situation from stale, and needs different words.
  if (!data.warehouse_loaded) {
    return (
      <Strip tone="warn" icon={<AlertTriangle size={13} />}>
        <strong>No data loaded yet.</strong> The warehouse is empty — every figure
        below reads zero because nothing has been extracted, not because nothing
        happened.
        {data.next_due_seconds != null && (
          <> The first extract runs in about {Math.ceil(data.next_due_seconds / 60)} min,
          or start it now with <code className="font-mono">./deploy.sh etl</code>.</>
        )}
      </Strip>
    );
  }

  if (data.any_interrupted) {
    // A load killed mid-flight leaves its watermark at "running". Saying
    // "in progress" forever would be worse than saying it stopped.
    const stuck = Object.entries(data.sources)
      .filter(([, s]) => s.status === "interrupted")
      .map(([name]) => name);
    return (
      <Strip tone="warn" icon={<AlertTriangle size={13} />}>
        <strong>An extract was interrupted</strong> ({stuck.join(", ")}) — most
        likely a restart or redeploy while it was running. Figures below are
        partial. Restart it with <code className="font-mono">./deploy.sh etl</code>,
        or wait for the next scheduled run.
      </Strip>
    );
  }

  if (data.any_error) {
    const broken = Object.entries(data.sources)
      .filter(([, s]) => s.status === "error")
      .map(([name]) => name);
    return (
      <Strip tone="bad" icon={<AlertTriangle size={13} />}>
        <strong>Last extract failed</strong> for {broken.join(", ")}. Figures below
        are as of {dateTime(data.oldest_finished)} and are not advancing.
        See <span className="font-mono">System health</span>.
      </Strip>
    );
  }

  if (data.any_running) {
    return (
      <Strip tone="info" icon={<Loader2 size={13} className="animate-spin" />}>
        <strong>Extract running now.</strong> Figures below are as of{" "}
        {ago(data.oldest_finished)} and will update when it finishes.
      </Strip>
    );
  }

  const stale = data.next_due_seconds != null && data.next_due_seconds < 0;

  return (
    <Strip
      tone={stale ? "warn" : "ok"}
      icon={stale ? <AlertTriangle size={13} /> : <CheckCircle2 size={13} />}
    >
      Data as of <strong>{ago(data.oldest_finished)}</strong>
      <span className="text-ink-faint"> ({dateTime(data.oldest_finished)})</span>
      {" · "}
      {count(data.total_rows)} rows loaded
      {" · "}
      {stale ? (
        <span className="text-warn">
          next refresh overdue by {Math.abs(Math.round((data.next_due_seconds ?? 0) / 60))} min
        </span>
      ) : (
        <span className="inline-flex items-center gap-1">
          <RefreshCw size={11} />
          refreshes every {Math.round(data.refresh_interval_seconds / 60)} min
          {data.next_due_seconds != null &&
            `, next in ~${Math.max(0, Math.ceil(data.next_due_seconds / 60))} min`}
        </span>
      )}
    </Strip>
  );
}

function Strip({
  tone, icon, children,
}: {
  tone: "ok" | "info" | "warn" | "bad";
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  const toneClass = {
    ok: "bg-surface-alt text-ink-muted border-rule",
    info: "bg-petrol-soft text-petrol border-petrol/25",
    warn: "bg-warn-soft text-warn border-warn/25",
    bad: "bg-bad-soft text-bad border-bad/25",
  }[tone];

  return (
    <div className={`flex items-start gap-2 px-5 py-1.5 text-[12.5px] border-b ${toneClass}`}>
      <span className="mt-[2px] shrink-0">{icon}</span>
      <span className="leading-snug">{children}</span>
    </div>
  );
}
